/**
 * Recording Feature Flags
 * 
 * Feature flags for the stable 30 FPS canvas recording implementation.
 * All flags default to false for zero-regression safety.
 * Enable flags incrementally after testing each feature.
 * 
 * @see Base Docs/Canvas_Recording_Implementation_Plan.md
 */

// Read telemetry setting from environment variable (console-only debugging)
// Set REACT_APP_RECORDING_TELEMETRY=false in .env to disable
// Default to true for debugging hidden tab recording performance
const isTelemetryEnabled = process.env.REACT_APP_RECORDING_TELEMETRY !== 'false';

// Separate Streams Mode: Record webcam and screen as separate files (not combined canvas)
// Set REACT_APP_SEPARATE_STREAMS_MODE=true to enable
// Default: false (use existing canvas recording)
const isSeparateStreamsModeEnabled = process.env.REACT_APP_SEPARATE_STREAMS_MODE === 'true';

export const RECORDING_FLAGS = {
  // Phase 1: Critical Fixes
  OPTIMIZED_CACHING: false,       // DISABLED - let Fabric handle caching
  SKIP_OFFSCREEN: false,          // DISABLED - let Fabric handle offscreen optimization
  VISIBILITY_LISTENER: false,     // DISABLED - no visibility tracking needed
  WORKER_SCHEDULER: false,        // DISABLED - no worker-driven frame scheduling
  DRIFT_COMPENSATION: false,      // DISABLED - no drift compensation
  HIDDEN_RENDER_ALL: false,       // DISABLED - no synchronous renderAll() in hidden mode
  VISIBILITY_BRIDGE: false,       // DISABLED - no RAF→Worker transition

  // Phase 2: MSTG Integration
  // DISABLED: MSTG causes frozen first second due to frame duplication using stale bitmap
  // captureStream(30) is simpler and works correctly - Vimeo uses this approach
  MSTG_ENABLED: false,           // DISABLED - use captureStream instead for simplicity
  MSTG_FALLBACK: false,          // DISABLED - no MSTG fallback needed
  FRAME_DUPLICATION: false,      // DISABLED - captureStream handles frame timing automatically
  RENDER_BUDGET_MS: 28,          // Max milliseconds for render before triggering duplication

  // Phase 3: Production Hardening
  TELEMETRY_ENABLED: false, // DISABLED - no telemetry to reduce overhead
  MEMORY_GUARDS: false,          // DISABLED - no aggressive cleanup
  DRIFT_ALERTS: false,           // DISABLED - no drift alerts
  
  // Phase 4: Canvas Performance Enhancement
  SILENT_AUDIO_LOOP: false,      // DISABLED - no silent audio loop
  AV_DRIFT_TRACKING: false,      // DISABLED - no AV drift tracking
  
  // Phase 5: OffscreenCanvas Worker Rendering
  // DISABLED: Conflicts with VIMEO_DIRECT_RENDER - they both try to handle hidden tab rendering
  OFFSCREEN_CANVAS: false,       // Use OffscreenCanvas in Web Worker for hidden-tab rendering
  
  // Phase 6: Vimeo-Style CaptureStream Rendering (Hidden Tab 30 FPS Fix)
  // DISABLED: Let Fabric handle all rendering naturally
  VIMEO_CAPTURE_STREAM: false,   // DISABLED - no captureStream + direct Canvas 2D rendering
  VIMEO_DIRECT_RENDER: false,    // DISABLED - let Fabric.js handle all rendering
  VIMEO_KEYFRAME_INTERVAL: false, // DISABLED - no keyframe interval
  VIMEO_TIMESLICE_10S: false,    // DISABLED - use default timeslice
  VIMEO_FALLBACK_ENABLED: false, // DISABLED - no MSTG fallback
  VIMEO_TELEMETRY: false, // DISABLED - no telemetry
  
  // Phase 7: Separate Stream Recording
  // Records webcam and screen as separate files instead of combined canvas
  // Enables post-recording layout changes and preserves original quality
  SEPARATE_STREAMS_MODE: isSeparateStreamsModeEnabled, // Master toggle for separate streams feature
  
  // Phase 8: Compositor Player for Separate Stream Playback
  // Uses Remotion-based player to recompose separate streams for preview
  // Provides pixel-perfect playback matching original recording layout
  USE_COMPOSITOR_PLAYER: true, // Enable compositor for separate stream previews
} as const;

export type RecordingFlagsType = typeof RECORDING_FLAGS;
